import { useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button } from '@blade/ui-kit'
import { DomUtil, environment, useBreakpoint } from '@root/shared'
import {
  AppsIcon,
  Dialog,
  DownloadIcon,
  GooglePlayStoreIcon,
  WindowsStoreIcon,
  WebsiteLayoutIcon,
  AppleIcon,
  AppStoreIcon,
  mediaMaxMobile,
} from '@root/shared/ui-kit'

import { ApplinkURL } from '../..'
import DEVICE_DOWNLOAD_IMAGE_MOBILE from '../assets/device-download-mobile.webp'
import DEVICE_DOWNLOAD_IMAGE from '../assets/device-download.webp'
import DEVICES_BG_CIRCLES from '../assets/devices-circles.svg'
import { downloadDeeplinkAtom } from '../atoms'

import { DownloadDialogHeader } from './download-dialog-header'

export const DownloadDialog: React.FC = () => {
  const { t } = useTranslation()
  const [deepLink, setDeepLink] = useAtom(downloadDeeplinkAtom)
  const device = useBreakpoint()

  const handleClose = () => setDeepLink(undefined)
  const handleDownload = () => window.open(environment.downloadUrl, '_blank')
  const handleOpenApp = () => {
    if (deepLink) {
      ApplinkURL.trigger(deepLink)
    }
  }
  useEffect(() => {
    if (deepLink) {
      ApplinkURL.trigger(deepLink)
    }
  }, [deepLink])

  return (
    <Root
      open={!!deepLink}
      onOpenChange={handleClose}
      describedBy={t('deep-link.download-dialog.title')}
    >
      <DownloadDialogHeader onClose={handleClose} />

      <Body>
        <Actions>
          <ButtonApp
            size="m"
            variant="glass"
            radius="rounded"
            iconLeft={<WebsiteLayoutIcon />}
            onClick={handleClose}
            css={css`
              grid-area: website;
            `}
          >
            {t('deep-link.download-dialog.continue-on-website')}
          </ButtonApp>
          <ButtonApp
            size="m"
            variant="glass"
            radius="rounded"
            iconLeft={<AppsIcon />}
            onClick={handleOpenApp}
            css={css`
              grid-area: app;
            `}
          >
            {t('deep-link.download-dialog.open-in-app')}
          </ButtonApp>
          <ButtonDownload
            size="m"
            variant="glass"
            radius="rounded"
            iconLeft={<DownloadIcon />}
            onClick={handleDownload}
            css={css`
              grid-area: download;
            `}
          >
            {t('deep-link.download-dialog.download-app')}
            <span>
              <WindowsStoreIcon size={20} />
              <GooglePlayStoreIcon size={20} />
              <AppleIcon size={20} />
              <AppStoreIcon size={20} />
            </span>
          </ButtonDownload>
        </Actions>
        {device === 'mobile' ? (
          <ImageMobile
            src={DEVICE_DOWNLOAD_IMAGE_MOBILE}
            alt={t('deep-link.download-dialog.title')}
            title={t('deep-link.download-dialog.title')}
          />
        ) : (
          <Image
            src={DEVICE_DOWNLOAD_IMAGE}
            alt={t('deep-link.download-dialog.title')}
            title={t('deep-link.download-dialog.title')}
          />
        )}
      </Body>
      <ImageGlow />
      <LeftGlow />
      <RightGlow />
      <BackgroundGlow />
    </Root>
  )
}

const Root = styled(Dialog)`
  border: 1px solid var(--backgroundA-3);
  background: var(--backgroundA-2);

  width: 460px;
  height: 440px;

  ${mediaMaxMobile(css`
    width: calc(100vw - 24px);
    height: 580px;
  `)}

  overflow: hidden;
  border-radius: 20px;

  backdrop-filter: blur(50px);

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const Body = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;

  background: ${DomUtil.cssUrl(DEVICES_BG_CIRCLES)};
  background-size: cover;

  display: grid;
  height: 100%;
`

const Image = styled.img`
  position: absolute;

  right: 0;
  bottom: 0;
  width: 440px;
  left: 20px;

  z-index: 1;
`

const ImageMobile = styled.img`
  position: absolute;
  left: calc(50% - 231px / 2);
  bottom: -190px;
  width: 231px;
  box-shadow: 0 10px 40px 0 var(--appearanceA-5);

  z-index: 1;
`

const Actions = styled.div`
  display: grid;
  grid-template-areas:
    'website app'
    'download download';

  gap: 20px;
  padding: 22px 20px;

  ${mediaMaxMobile(css`
    gap: 10px;
    grid-template-areas:
      'website'
      'app'
      'download';
  `)}

  z-index: 2;
`

const ButtonApp = styled(Button)`
  box-shadow: inset 0 0 0 1px var(--backgroundA-2);
  background: var(--backgroundA-2);
  background-image: radial-gradient(
    64.13% 78.12% at 49.7% 100%,
    var(--backgroundA-2) 0%,
    transparent 100%
  );

  display: grid;
  grid-template-columns: auto 1fr;
  justify-items: start;
  padding-inline: 22px;

  height: 54px;

  color: var(--text-12);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;

  border-radius: 10px;
  backdrop-filter: blur(20px);
  /* Safari: unfortunately, due to fundamental implementation differences, pixel-perfect parity is impossible. */
  /* Use these adjustments to reach an acceptable balance. */
  -webkit-backdrop-filter: blur(0px);

  html[lang='ru'] & {
    font-size: 12px;
    padding-inline: 16px;
  }
`

const ButtonDownload = styled(ButtonApp)`
  grid-column: 1 / 3;
  grid-template-columns: auto 1fr auto;

  display: grid;
  justify-items: start;

  > span {
    display: grid;
    grid-template-columns: repeat(4, auto);
    gap: 8px;
  }
`

const BackgroundGlow = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 370px;

  &:before {
    content: '';
    opacity: 0.6;
    position: absolute;
    top: 200px;
    left: 0;
    width: 100%;
    height: 410px;
    border-radius: 430px;
    background: radial-gradient(
      79.14% 79.14% at 74.58% 24.78%,
      var(--primary-8) 0%,
      var(--primaryA-4) 100%
    );
    filter: blur(100px);
  }
`

const LeftGlow = styled.div`
  background: var(--primary-8);
  width: 140px;
  height: 160px;
  opacity: 0.2;

  filter: blur(50px);
  position: absolute;
  left: -40px;
  top: -40px;
`

const RightGlow = styled.div`
  background: var(--primaryA-4);

  width: 125px;
  height: 125px;

  opacity: 0.1;
  filter: blur(70px);

  position: absolute;
  right: -30px;
  top: 100px;
`

const ImageGlow = styled.div`
  background: linear-gradient(
    180deg,
    var(--primary-3) 0%,
    var(--primary-8) 100%
  );
  filter: blur(30px);

  position: absolute;
  bottom: -70px;
  left: 0;
  width: 100%;
  height: 108px;

  z-index: 1;
`
